import { useState } from 'react';
import { BookingCalendar } from './BookingCalendar';
import IntakeStep1BasicInfo from './coaching/IntakeStep1BasicInfo';
import IntakeStep2Struggles from './coaching/IntakeStep2Struggles';
import IntakeStep3Goals from './coaching/IntakeStep3Goals';
import IntakeStep5Review from './coaching/IntakeStep5Review';
import { CoachingFormData, EMPTY_FORM_DATA, INTAKE_STEPS } from './coaching/types';

interface CoachingApplicationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const FORM_SUBMIT_URL = 'https://readdy.ai/api/form/d6te8jncrcujsk0v189g';

export default function CoachingApplicationModal({ isOpen, onClose }: CoachingApplicationModalProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<CoachingFormData>(EMPTY_FORM_DATA);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showConfirmClose, setShowConfirmClose] = useState(false);
  const [hasScheduled, setHasScheduled] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);

  if (!isOpen) return null;

  const update = (updates: Partial<CoachingFormData>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  };

  const hasAnyData = () =>
    formData.full_name.trim() !== '' ||
    formData.email.trim() !== '' ||
    formData.daily_life_description.trim() !== '' ||
    formData.seek_support_reason.trim() !== '';

  const handleClose = () => {
    if (hasAnyData() && !submitSuccess) {
      setShowConfirmClose(true);
    } else {
      doClose();
    }
  };

  const doClose = () => {
    setShowConfirmClose(false);
    onClose();
    // reset after animation
    setTimeout(() => {
      setCurrentStep(1);
      setFormData(EMPTY_FORM_DATA);
      setErrors({});
      setHasScheduled(false);
      setSubmitError('');
      setSubmitSuccess(false);
    }, 300);
  };

  // ── Validators ──────────────────────────────────────────────────────────────
  const validateStep1 = (): boolean => {
    const e: Record<string, string> = {};
    if (!formData.full_name.trim()) e.full_name = 'Full name is required.';
    if (!formData.email.trim()) {
      e.email = 'Email address is required.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      e.email = 'Please enter a valid email.';
    }
    if (!formData.location.trim()) e.location = 'City & State is required.';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateStep2 = (): boolean => {
    const e: Record<string, string> = {};
    if (!formData.daily_life_description.trim()) {
      e.daily_life_description = 'Please share how ADHD is affecting your daily life.';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateStep3 = (): boolean => {
    const e: Record<string, string> = {};
    if (!formData.coaching_hope.trim()) {
      e.coaching_hope = 'Please share what you hope coaching will change for you.';
    }
    if (!formData.readiness) {
      e.readiness = 'Please select your readiness level.';
    }
    if (!formData.fit_acknowledgment) {
      e.fit_acknowledgment = 'Please acknowledge the coaching agreement to continue.';
    }
    if (!formData.seek_support_reason.trim()) {
      e.seek_support_reason = 'Please share what brought you here today.';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // ── Navigation ───────────────────────────────────────────────────────────────
  const handleNext = () => {
    setErrors({});
    if (currentStep === 1 && !validateStep1()) return;
    if (currentStep === 2 && !validateStep2()) return;
    if (currentStep === 3 && !validateStep3()) return;
    setCurrentStep((s) => s + 1);
  };

  const handleBack = () => {
    setErrors({});
    setCurrentStep((s) => s - 1);
  };

  // ── Submit ───────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError('');
    try {
      const params = new URLSearchParams();
      params.append('full_name', formData.full_name);
      params.append('email', formData.email);
      params.append('location', formData.location);
      if (formData.phone) params.append('phone', formData.phone);
      params.append('struggles', formData.struggles.join(', '));
      if (formData.struggles_other) params.append('struggles_other', formData.struggles_other);
      if (formData.adhd_awareness) params.append('adhd_awareness', formData.adhd_awareness);
      params.append('life_impact_areas', formData.life_impact_areas.join(', '));
      params.append('daily_life_description', formData.daily_life_description);
      params.append('desired_changes', formData.desired_changes.join(', '));
      params.append('coaching_hope', formData.coaching_hope);
      params.append('readiness', formData.readiness);
      if (formData.obstacles) params.append('obstacles', formData.obstacles);
      params.append('fit_acknowledgment', 'Acknowledged');
      params.append('seek_support_reason', formData.seek_support_reason);
      if (hasScheduled) params.append('intake_call_scheduled', 'Yes');

      const response = await fetch(FORM_SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      if (!response.ok) throw new Error('Submission failed. Please try again.');
      setSubmitSuccess(true);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isLastStep = currentStep === INTAKE_STEPS.length;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <form
          data-readdy-form
          className="bg-[#FAF7F2] rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto pointer-events-auto"
          onClick={(e) => e.stopPropagation()}
          onSubmit={(e) => e.preventDefault()}
        >
          {/* Header */}
          <div className="sticky top-0 bg-[#FAF7F2] border-b border-[#C9A96E]/20 px-6 md:px-8 py-5 flex items-center justify-between z-10">
            <div>
              <h2
                className="text-2xl md:text-3xl font-light text-[#1E2D5E]"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                Apply for ADHD Coaching
              </h2>
              <p className="text-sm text-[#1E2D5E]/55 mt-0.5">
                {submitSuccess ? 'Application submitted' : `Step ${currentStep} of ${INTAKE_STEPS.length}`}
              </p>
            </div>
            <button
              type="button"
              onClick={handleClose}
              className="w-10 h-10 flex items-center justify-center rounded-full bg-[#F5F1E8] text-[#1E2D5E] hover:bg-[#C9A96E]/20 transition-colors cursor-pointer"
              aria-label="Close"
            >
              <i className="ri-close-line text-xl" />
            </button>
          </div>

          {/* Step Indicator */}
          {!submitSuccess && (
            <div className="px-6 md:px-8 py-5 border-b border-[#C9A96E]/10">
              <div className="flex items-center gap-1">
                {INTAKE_STEPS.map((label, i) => (
                  <div key={i} className="flex items-center gap-1 flex-1">
                    <div className="flex flex-col items-center gap-1 flex-1 min-w-0">
                      <div
                        className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium transition-all flex-shrink-0 ${
                          i + 1 < currentStep
                            ? 'bg-[#C9A96E] text-white'
                            : i + 1 === currentStep
                            ? 'bg-[#1E2D5E] text-white'
                            : 'bg-[#E8E2D6] text-[#1E2D5E]/40'
                        }`}
                      >
                        {i + 1 < currentStep ? <i className="ri-check-line text-xs" /> : i + 1}
                      </div>
                      <span
                        className={`text-xs whitespace-nowrap font-medium hidden sm:block ${
                          i + 1 === currentStep ? 'text-[#1E2D5E]' : 'text-[#1E2D5E]/35'
                        }`}
                      >
                        {label}
                      </span>
                    </div>
                    {i < INTAKE_STEPS.length - 1 && (
                      <div
                        className={`h-px flex-1 mb-4 ${i + 1 < currentStep ? 'bg-[#C9A96E]' : 'bg-[#E8E2D6]'}`}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Content */}
          <div className="px-6 md:px-8 py-8">
            {currentStep === 1 && (
              <IntakeStep1BasicInfo data={formData} onChange={update} errors={errors} />
            )}
            {currentStep === 2 && (
              <IntakeStep2Struggles data={formData} onChange={update} errors={errors} />
            )}
            {currentStep === 3 && (
              <IntakeStep3Goals data={formData} onChange={update} errors={errors} />
            )}
            {currentStep === 4 && (
              <div>
                <h3
                  className="text-xl md:text-2xl font-light text-[#1E2D5E] mb-1"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  Schedule Your 20-Minute Intake Call
                </h3>
                <p className="text-sm text-[#1E2D5E]/60 mb-2 leading-relaxed">
                  Choose a time for a brief conversation with Lauren. She&apos;ll review your application and confirm within 24–48 hours.
                </p>
                <div className="bg-[#FDF8F0] border border-[#C9A96E]/20 rounded-lg px-4 py-3 mb-6 flex items-center gap-2">
                  <i className="ri-calendar-line text-[#C9A96E]" />
                  <p className="text-xs text-[#1E2D5E]/65">
                    Selecting a time is optional here — Lauren will confirm the final time after reviewing your application.
                  </p>
                </div>
                <BookingCalendar
                  timeslotsApi="https://readdy.ai/api/public/calendar/timeslots?calendarId=coaching-intake"
                  appointmentsApi="https://readdy.ai/api/public/calendar/appointments?calendarId=coaching-intake"
                  texts={{
                    selectDate: 'Select a Date',
                    chooseTime: 'Choose a Time',
                    reviewDetails: 'Your Info',
                    fullName: 'Full Name',
                    phoneNumber: 'Phone Number',
                    confirmBooking: 'Confirm Intake Call',
                    modifyTime: 'Change Time',
                    back: 'Back',
                    noSlotsAvailable: 'No time slots available for this date.',
                    bookingSuccess: 'Time preference saved! Lauren will confirm your call.',
                    bookingError: 'There was an error. Please try again.',
                    loading: 'Loading...',
                  }}
                  containerStyle="bg-transparent"
                />
              </div>
            )}
            {currentStep === 5 && (
              <IntakeStep5Review
                data={formData}
                onEdit={setCurrentStep}
                onSubmit={handleSubmit}
                isSubmitting={isSubmitting}
                submitError={submitError}
                submitSuccess={submitSuccess}
                hasScheduled={hasScheduled}
              />
            )}

            {/* Navigation */}
            {!submitSuccess && (
              <div className={`flex mt-8 ${currentStep > 1 ? 'justify-between' : 'justify-end'}`}>
                {currentStep > 1 && (
                  <button
                    type="button"
                    onClick={handleBack}
                    className="px-7 py-3 bg-[#F5F1E8] text-[#1E2D5E] rounded-full text-sm font-medium tracking-wide hover:bg-[#C9A96E]/20 transition-all duration-300 whitespace-nowrap cursor-pointer"
                  >
                    ← Back
                  </button>
                )}
                {!isLastStep && (
                  <button
                    type="button"
                    onClick={handleNext}
                    className="px-8 py-3 bg-[#1E2D5E] text-[#FAF7F2] rounded-full text-sm font-medium tracking-wide hover:bg-[#2A3F7A] transition-all duration-300 whitespace-nowrap cursor-pointer"
                  >
                    {currentStep === 4 ? 'Review Application →' : 'Next →'}
                  </button>
                )}
              </div>
            )}

            {submitSuccess && (
              <div className="mt-6 text-center">
                <button
                  type="button"
                  onClick={doClose}
                  className="px-8 py-3 bg-[#C9A96E] text-white rounded-full text-sm font-medium hover:bg-[#B89858] transition-colors cursor-pointer whitespace-nowrap"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </form>
      </div>

      {/* Confirm Close Dialog */}
      {showConfirmClose && (
        <>
          <div className="fixed inset-0 bg-black/60 z-[60]" onClick={() => setShowConfirmClose(false)} />
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 pointer-events-none">
            <div className="bg-white rounded-xl max-w-md w-full p-6 pointer-events-auto">
              <h3 className="text-xl font-medium text-[#1E2D5E] mb-2">Discard Application?</h3>
              <p className="text-sm text-[#1E2D5E]/70 mb-6">
                You have unsaved progress. Are you sure you want to close and lose your answers?
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => setShowConfirmClose(false)}
                  className="px-5 py-2.5 bg-[#F5F1E8] text-[#1E2D5E] rounded-lg text-sm font-medium hover:bg-[#C9A96E]/20 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Keep Editing
                </button>
                <button
                  type="button"
                  onClick={doClose}
                  className="px-5 py-2.5 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Discard
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}
